import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { GraduationCap } from 'lucide-react';
import { assetToast } from '@xdocs/i18n';
import { errorAlert } from '@xdocs/schemas';
import type { DifficultyLevel, JobAccepted } from '@xdocs/schemas';
import { requestTyped } from '@xdocs/ws-protocol';
import { Badge, ListRow, SegmentedControl, cn, useI18n, useToast } from '@xdocs/ui';
import { useJobSnapshot } from '../jobs/index.js';
import { useWs } from '../../ws/WsProvider.js';
import { EnhancementPanelShell } from './EnhancementPanelShell.js';
import type { EnhancementPanelPhase } from './EnhancementPanelShell.js';
import { useActionEstimate } from './useActionEstimate.js';
import { useEnhancementT } from './useEnhancementT.js';
import styles from './DifficultyPanel.module.css';

export interface DifficultyPanelProps {
  documentId: string;
  /** Required — names the asset in the done/error toasts (§16.1). */
  documentTitle: string;
  versionId: string;
  /** Current reading-difficulty attribute (`documents.difficultyLevel`), shown
   * read-only everywhere per F4 AC; this panel is the elevate/change control. */
  currentLevel: DifficultyLevel;
  className?: string | undefined;
}

const LEVELS: readonly DifficultyLevel[] = ['basic', 'intermediate', 'advanced', 'mastery'];

/**
 * F4 — document difficulty levels (elevate to mastery, PRO). Self-contained
 * trigger + panel. "Elevating already-at-mastery: disable" (F4 AC) is
 * satisfied by disabling the currently-selected level as a target — the CTA
 * itself only enables once a DIFFERENT level is chosen.
 */
export function DifficultyPanel({ documentId, documentTitle, versionId, currentLevel, className }: DifficultyPanelProps): ReactElement {
  const { t } = useEnhancementT();
  const { locale } = useI18n();
  const { push: pushToast } = useToast();
  const { wsClient } = useWs();

  const [open, setOpen] = useState(false);
  // Only ever 'form' | 'starting' | 'upsell' | 'running' — terminal
  // 'done'/'error' is DERIVED from the live job snapshot below (react-hooks
  // purity: never setState synchronously inside an effect).
  const [phase, setPhase] = useState<EnhancementPanelPhase>('form');
  const [targetLevel, setTargetLevel] = useState<DifficultyLevel>(currentLevel);
  const [jobId, setJobId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const hasChanged = targetLevel !== currentLevel;
  const { estimate } = useActionEstimate('difficulty.estimate', open && hasChanged ? { documentId, versionId, targetLevel } : null);
  const { snapshot } = useJobSnapshot(jobId);

  const jobSucceeded = phase === 'running' && snapshot?.job.status === 'succeeded';
  const jobFailedTerminal =
    phase === 'running' && snapshot?.job.status === 'failed' && snapshot.job.attempts >= snapshot.job.maxAttempts;
  const displayPhase: EnhancementPanelPhase = jobSucceeded ? 'done' : jobFailedTerminal ? 'error' : phase;
  const displayErrorMessage = jobFailedTerminal ? (snapshot?.job.lastError ?? 'errors:code.INTERNAL') : submitError;

  const toastedForJobRef = useRef<string | null>(null);
  useEffect(() => {
    if (jobSucceeded && jobId && toastedForJobRef.current !== jobId) {
      pushToast(assetToast(locale, 'success', t('enhancement:difficulty.success'), documentTitle));
      toastedForJobRef.current = jobId;
    }
  }, [jobSucceeded, jobId, pushToast, locale, t, documentTitle]);

  function resetForm(): void {
    setPhase('form');
    setJobId(null);
    setSubmitError(null);
  }

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (!next) {
      resetForm();
      setTargetLevel(currentLevel);
    }
  }

  function handleStart(): void {
    if (!hasChanged) return;
    setPhase('starting');
    requestTyped(wsClient, 'document.difficulty', { documentId, versionId, targetLevel })
      .then((accepted: JobAccepted) => {
        setJobId(accepted.jobId);
        setPhase('running');
      })
      .catch((err: unknown) => {
        const parsed = errorAlert.safeParse(err);
        if (parsed.success && parsed.data.code === 'TIER_REQUIRED') {
          setPhase('upsell');
          return;
        }
        setPhase('error');
        setSubmitError(parsed.success ? parsed.data.message : 'errors:code.INTERNAL');
      });
  }

  const creditsLabel = estimate ? t('enhancement:cost.credits', { credits: estimate.credits }) : undefined;

  return (
    <>
      <ListRow
        icon={<GraduationCap size={16} aria-hidden="true" />}
        title={t('enhancement:difficulty.title')}
        trailing={<Badge tone="pro">{t('enhancement:pro.badge')}</Badge>}
        onClick={() => setOpen(true)}
        className={cn(className)}
      />
      <EnhancementPanelShell
        open={open}
        onOpenChange={handleOpenChange}
        title={t('enhancement:difficulty.title')}
        blurb={t('enhancement:difficulty.blurb')}
        phase={displayPhase}
        jobId={jobId}
        doneTitle={t('enhancement:difficulty.doneTitle')}
        errorMessage={displayErrorMessage}
        ctaLabel={t('enhancement:difficulty.cta')}
        creditsLabel={creditsLabel}
        ctaDisabled={!hasChanged}
        onStart={handleStart}
        onMaybeLater={() => handleOpenChange(false)}
        onDone={() => handleOpenChange(false)}
        onRetryFromError={resetForm}
      >
        <div className={styles['field']}>
          <SegmentedControl
            label={t('enhancement:difficulty.targetLevel')}
            value={targetLevel}
            onChange={setTargetLevel}
            options={LEVELS.map((level) => ({
              value: level,
              label: t(`enhancement:difficulty.level.${level}`),
              disabled: level === currentLevel,
            }))}
          />
          <p className={styles['currentNote']}>
            {t('enhancement:difficulty.currentLevel', { level: t(`enhancement:difficulty.level.${currentLevel}`) })}
          </p>
        </div>
      </EnhancementPanelShell>
    </>
  );
}
